import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { fmtDateTime, timeAgo, ticketCode } from '../utils/formatters';
import {
  ArrowLeft, Plus, MessageSquare, RefreshCw,
  CircleDot, CheckCircle2, AlertCircle, User, RotateCcw, Zap
} from 'lucide-react';

const C = {
  nav: 'var(--text-60a5fa)',
  border: 'var(--white-05)',
  bg: 'transparent',
  ptxt: 'var(--text-primary)',
  stxt: 'var(--text-muted)',
  card: 'var(--dark-panel)',
};

/* ── event icon/label map ─────────────────────────── */
function eventMeta(item) {
  const { type, data } = item;

  if (type === 'comment') {
    return {
      icon: <MessageSquare size={16} strokeWidth={2.5} />,
      title: 'Comment Added',
      detail: data.body,
      actor: data.author_username,
      internal: data.is_internal,
    };
  }

  // history event
  const d = data.details || {};
  const actor = data.actor_username || 'System';

  if (data.event_type === 'created') {
    return {
      icon: <Plus size={18} strokeWidth={3} />,
      title: 'Ticket Created',
      detail: '',
      actor,
    };
  }
  if (data.event_type === 'reopened') {
    return {
      icon: <RotateCcw size={16} strokeWidth={2.5} />,
      title: 'Ticket Reopened',
      detail: d.status ? `Was: ${d.status.from}` : '',
      actor,
    };
  }
  if (data.event_type === 'assigned') {
    return {
      icon: <User size={16} strokeWidth={2.5} />,
      title: 'Ticket Assigned',
      detail: '',
      actor,
    };
  }
  if (data.event_type === 'automation') {
    return {
      icon: <Zap size={16} strokeWidth={2.5} />,
      title: 'Automation Applied',
      detail: d.rule_name ? `Rule: ${d.rule_name}` : `Automated action`,
      actor: 'System',
    };
  }
  if (d.status) {
    return {
      icon: d.status.to === 'Resolved' || d.status.to === 'Closed'
        ? <CheckCircle2 size={16} strokeWidth={2.5} />
        : <RefreshCw size={16} strokeWidth={2.5} />,
      title: `Status: ${d.status.to}`,
      detail: `From: ${d.status.from}`,
      actor,
    };
  }
  if (d.priority) {
    return {
      icon: <AlertCircle size={16} strokeWidth={2.5} />,
      title: `Priority: ${d.priority.to}`,
      detail: `From: ${d.priority.from}`,
      actor,
    };
  }

  return {
    icon: <CircleDot size={16} strokeWidth={2.5} />,
    title: 'Ticket Updated',
    detail: '',
    actor,
  };
}

/* ─────────────────────────────────────────────────── */
export default function TicketTrackPage({ showToast }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request, loading } = useApi();

  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [timeline, setTimeline] = useState([]);

  const load = useCallback(async () => {
    try {
      const [tData, cData, hData] = await Promise.all([
        request(`/api/tickets/${id}`),
        request(`/api/comments/${id}`),
        request(`/api/tickets/${id}/history`),
      ]);
      setTicket(tData);
      setComments(cData);
      setHistory(hData);
    } catch (err) {
      showToast(err.message, 'error');
      navigate(`/tickets/${id}`, { replace: true });
    }
  }, [id, request, navigate, showToast]);

  useEffect(() => { load(); }, [load]);

  /* merge and sort timeline */
  useEffect(() => {
    const commentItems = comments.map(c => ({
      type: 'comment', id: `c-${c.id}`, ts: c.created_at, data: c,
    }));
    const historyItems = history.map(h => ({
      type: 'history', id: `h-${h.id}`, ts: h.created_at, data: h,
    }));
    const merged = [...commentItems, ...historyItems]
      .sort((a, b) => new Date(a.ts) - new Date(b.ts)); // oldest first on track page
    setTimeline(merged);
  }, [comments, history]);

  if (!ticket && loading) return (
    <div style={{ padding: 60, textAlign: 'center' }}><div className="spinner" /></div>
  );
  if (!ticket) return null;

  const code = ticketCode(ticket);

  return (
    <div style={{ background: C.bg, minHeight: 'calc(100vh - 80px)', padding: '40px' }}>
      <style>{`
        .track-header-top { display: flex; justify-content: space-between; align-items: center; width: 100%; margin-bottom: 32px; }
        .track-back-btn { display: flex; align-items: center; gap: 8px; background: var(--white-05); border: 1px solid ${C.border}; color: ${C.ptxt}; font-size: 14px; font-weight: 700; cursor: pointer; padding: 10px 18px; border-radius: 12px; transition: all 0.2s; backdrop-filter: blur(10px); }
        .track-back-btn:hover { background: var(--white-06); transform: translateX(-4px); }
        
        .track-refresh-btn { display: flex; align-items: center; gap: 8px; padding: 10px 18px; border: 1px solid ${C.border}; border-radius: 12px; background: rgba(96, 165, 250, 0.1); color: var(--text-60a5fa); font-size: 14px; font-weight: 800; cursor: pointer; transition: all 0.2s; backdrop-filter: blur(10px); }
        .track-refresh-btn:hover { background: rgba(96, 165, 250, 0.2); transform: translateY(-2px); }
        .track-refresh-btn svg.spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }

        .track-header-content { text-align: center; margin-bottom: 60px; padding: 40px; background: radial-gradient(circle at center, rgba(96, 165, 250, 0.1) 0%, transparent 70%); border-radius: 40px; }
        .track-header-label { font-size: 11px; font-weight: 900; color: var(--text-60a5fa); text-transform: uppercase; letter-spacing: 0.2em; margin-bottom: 16px; opacity: 0.8; }
        .track-header-title { margin: 0 0 12px; font-size: 36px; font-weight: 900; color: ${C.ptxt}; letter-spacing: -0.03em; }
        .track-header-id { font-size: 16px; color: ${C.stxt}; font-weight: 700; font-family: monospace; letter-spacing: 0.05em; }

        .track-timeline-wrap { position: relative; max-width: 900px; margin: 0 auto; padding-bottom: 80px; }
        
        .track-event { display: flex; width: 100%; position: relative; margin-bottom: 32px; align-items: flex-start; }
        
        .track-vertical-line { position: absolute; left: 50%; top: 42px; bottom: -80px; width: 2px; background: linear-gradient(to bottom, var(--text-60a5fa), rgba(96, 165, 250, 0.1)); transform: translateX(-50%); z-index: 0; opacity: 0.3; }
        .track-vertical-arrow { position: absolute; left: 50%; bottom: -58px; transform: translateX(-50%); border-left: 6px solid transparent; border-right: 6px solid transparent; border-top: 8px solid var(--text-60a5fa); z-index: 1; opacity: 0.5; }

        .track-half { flex: 1; position: relative; display: flex; align-items: flex-start; }
        .track-half.left { justify-content: flex-end; padding-right: 60px; }
        .track-half.right { justify-content: flex-start; padding-left: 60px; }

        /* Horizontal Connectors */
        .track-half.left.has-card::before { content: ''; position: absolute; right: 0; top: 42px; transform: translateY(-50%); width: 60px; height: 2px; background: rgba(96, 165, 250, 0.3); z-index: 0; }
        .track-half.left.has-card::after { content: ''; position: absolute; right: 54px; top: 42px; transform: translateY(-50%); border-top: 6px solid transparent; border-bottom: 6px solid transparent; border-right: 8px solid var(--text-60a5fa); z-index: 1; }

        .track-half.right.has-card::before { content: ''; position: absolute; left: 0; top: 42px; transform: translateY(-50%); width: 60px; height: 2px; background: rgba(96, 165, 250, 0.3); z-index: 0; }
        .track-half.right.has-card::after { content: ''; position: absolute; left: 54px; top: 42px; transform: translateY(-50%); border-top: 6px solid transparent; border-bottom: 6px solid transparent; border-left: 8px solid var(--text-60a5fa); z-index: 1; }

        .track-node-wrapper { position: absolute; left: 50%; top: 0; transform: translateX(-50%); z-index: 2; display: flex; flex-direction: column; align-items: center; }
        .track-node { position: relative; width: 44px; height: 44px; border-radius: 50%; margin-top: 20px; display: flex; align-items: center; justify-content: center; transition: all 0.3s; z-index: 2; background: rgba(15, 15, 22, 0.9); border: 2px solid rgba(96, 165, 250, 0.3); color: var(--text-60a5fa); box-shadow: 0 0 15px rgba(96, 165, 250, 0.1); }
        
        .node-start { border-color: var(--text-3b82f6); color: var(--text-3b82f6); box-shadow: 0 0 20px rgba(16, 185, 129, 0.2); }
        .node-normal { }
        .node-latest { background: var(--text-60a5fa); border: 4px solid #fff; color: #fff; width: 52px; height: 52px; margin-top: 16px; box-shadow: 0 0 30px rgba(96, 165, 250, 0.5); transform: scale(1.1); }

        .latest-node-label { position: absolute; top: -12px; left: 50%; transform: translateX(-50%); font-size: 10px; font-weight: 900; color: #fff; text-transform: uppercase; letter-spacing: 0.1em; background: var(--text-60a5fa); padding: 4px 12px; border-radius: 20px; white-space: nowrap; z-index: 3; box-shadow: 0 4px 15px rgba(96, 165, 250, 0.4); }

        .track-card { background: ${C.card}; border: 1px solid ${C.border}; border-radius: 20px; padding: 24px; width: 100%; max-width: 380px; box-shadow: 0 10px 30px var(--black-20); position: relative; margin-top: 12px; transition: all 0.3s; backdrop-filter: blur(20px); }
        .track-card:hover { border-color: rgba(96, 165, 250, 0.4); background: var(--white-03); transform: translateY(-4px); }
        .track-card-latest { border: 2px solid var(--text-60a5fa); box-shadow: 0 15px 40px rgba(96, 165, 250, 0.2); }

        .track-title { font-size: 16px; font-weight: 800; color: ${C.ptxt}; display: flex; align-items: center; gap: 10px; margin-bottom: 10px; letter-spacing: -0.01em; }
        .track-detail { font-size: 14px; color: ${C.stxt}; margin-bottom: 16px; line-height: 1.7; white-space: pre-wrap; font-weight: 500; }
        
        .track-meta { display: flex; align-items: center; gap: 10px; font-size: 12px; color: ${C.stxt}; opacity: 0.7; }
        .track-meta-dot { width: 4px; height: 4px; border-radius: 50%; background: var(--text-60a5fa); opacity: 0.5; }
        
        .track-internal-badge { font-size: 10px; font-weight: 800; background: rgba(96, 165, 250, 0.1); color: var(--text-60a5fa); padding: 3px 8px; border-radius: 6px; border: 1px solid rgba(96, 165, 250, 0.2); letter-spacing: 0.05em; }
      `}</style>

      {/* Top Navigation */}
      <div className="track-header-top">
        <button className="track-back-btn" onClick={() => navigate(`/tickets/${id}`, { replace: true })}>
          <ArrowLeft size={16} /> Back to Ticket
        </button>
        <button 
          className="track-refresh-btn" 
          onClick={load} 
          disabled={loading}
          style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
        >
          <RefreshCw size={14} className={loading ? 'spin' : ''} /> {loading ? 'Refreshing...' : 'Refresh Feed'}
        </button>
      </div>

      {/* Header Area */}
      <div className="track-header-content">
        <div className="track-header-label">Activity Flow</div>
        <h1 className="track-header-title">{ticket.title}</h1>
        <div className="track-header-id">#{code}</div>
      </div>

      {/* Flowchart Timeline */}
      <div className="track-timeline-wrap">
        {timeline.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: C.stxt, fontSize: 14, fontStyle: 'italic' }}>
            No process steps recorded yet.
          </div>
        )}

        {timeline.map((item, idx) => {
          const meta = eventMeta(item);
          const isLatest = idx === timeline.length - 1;
          const isFirst = idx === 0;
          const isLeft = idx % 2 === 0; // Alternating zig-zag
          const isRight = !isLeft;

          return (
            <div key={item.id} className="track-event">
              {/* Connectors to next node */}
              {!isLatest && (
                <>
                  <div className="track-vertical-line" />
                  <div className="track-vertical-arrow" />
                </>
              )}

              {/* Left Side */}
              <div className={`track-half left ${isLeft ? 'has-card' : ''}`}>
                {isLeft && (
                  <div className={`track-card ${isLatest ? 'track-card-latest' : ''}`}>
                    <div className="track-title">
                      {meta.title}
                      {meta.internal && <span className="track-internal-badge">INTERNAL</span>}
                    </div>
                    {meta.detail && (
                      <div className="track-detail">{meta.detail}</div>
                    )}
                    <div className="track-meta">
                      <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>{meta.actor}</span>
                      <div className="track-meta-dot" />
                      <span>{timeAgo(item.ts)}</span>
                      <div className="track-meta-dot" />
                      <span>{fmtDateTime(item.ts)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Center Node */}
              <div className="track-node-wrapper">
                {isLatest && <div className="latest-node-label">Current State</div>}
                <div className={`track-node ${isLatest ? 'node-latest' : isFirst ? 'node-start' : 'node-normal'}`}>
                  {meta.icon}
                </div>
              </div>

              {/* Right Side */}
              <div className={`track-half right ${isRight ? 'has-card' : ''}`}>
                {isRight && (
                  <div className={`track-card ${isLatest ? 'track-card-latest' : ''}`}>
                    <div className="track-title">
                      {meta.title}
                      {meta.internal && <span className="track-internal-badge">INTERNAL</span>}
                    </div>
                    {meta.detail && (
                      <div className="track-detail">{meta.detail}</div>
                    )}
                    <div className="track-meta">
                      <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>{meta.actor}</span>
                      <div className="track-meta-dot" />
                      <span>{timeAgo(item.ts)}</span>
                      <div className="track-meta-dot" />
                      <span>{fmtDateTime(item.ts)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}



